import React, { useMemo, useEffect, useState, useRef } from "react";
import { schoolLeaderboard, normalizeSchool } from "../../lib/aggregateAnalytics";
import {
  buildOutreachRows,
  filterOutreachRows,
  exportLeaderboardCsv,
  parseSchoolContactsCsv,
  mergeCsvSchoolImport,
  OUTREACH_FILTERS,
  normalizeWhatsApp,
} from "../../lib/schoolOutreach";
import SchoolContactModal from "./SchoolContactModal";
import {
  summarizeQuizBySchool,
  enrichRowQuizMeta,
} from "../../lib/quizSeason";
import QuizSeasonCell from "../outreach/QuizSeasonCell";

const MEDAL = ["🥇", "🥈", "🥉"];

function useMobile(bp = 700) {
  const [m, setM] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width:${bp}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${bp}px)`);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [bp]);
  return m;
}

function IctBadge({ registered }) {
  if (!registered) return <span className="lb-none">—</span>;
  return (
    <span className="ict-badge" title="Registered for Most Popular ICT Society">
      ✓
    </span>
  );
}

function ContactCell({ row, onEdit }) {
  const wa = normalizeWhatsApp(row.whatsapp);
  const copyNumber = () => {
    if (!wa) return;
    navigator.clipboard?.writeText(wa).then(() => {
      /* copied */
    });
  };
  if (!wa) {
    return (
      <button type="button" className="lb-contact-add" onClick={() => onEdit(row)}>
        + Add number
      </button>
    );
  }
  return (
    <div className="lb-contact-cell">
      <span className="lb-contact-num" title={wa}>
        {row.contactName ? `${row.contactName}` : wa}
      </span>
      <div className="lb-contact-actions">
        <button
          type="button"
          className="lb-copy-btn"
          onClick={copyNumber}
          title={`Copy ${wa}`}
        >
          #
        </button>
        <a
          href={row.waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="lb-wa-btn"
          title="Open WhatsApp with smart message"
        >
          WA
        </a>
        <button type="button" className="lb-edit-btn" onClick={() => onEdit(row)}>
          Edit
        </button>
      </div>
    </div>
  );
}

export default function SchoolLeaderboard({
  records,
  schoolContacts = [],
  manualSchools = [],
  schoolState = {},
  onQuizConfirm,
  onSaveOutreach,
  savingOutreach = false,
}) {
  const isMobile = useMobile();
  const [filterId, setFilterId] = useState("all");
  const [contactSchool, setContactSchool] = useState(null);
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState("");
  const [newSchoolWa, setNewSchoolWa] = useState("");
  const [importStatus, setImportStatus] = useState(null);
  const csvInputRef = useRef(null);

  const baseRows = useMemo(() => schoolLeaderboard(records), [records]);

  const registeredKeys = useMemo(
    () => new Set(baseRows.map((r) => r.key)),
    [baseRows]
  );

  const { current: quizCurrentBySchool, stale: quizStaleBySchool } = useMemo(
    () => summarizeQuizBySchool(records),
    [records]
  );

  const ictBySchool = useMemo(() => {
    const s = new Set();
    for (const r of records) {
      if (r.collection !== "ict_society_registrations") continue;
      const raw =
        (r.schoolKey ?? (r.schoolName ?? "").trim().toLowerCase()) || "(unknown)";
      s.add(raw);
    }
    return s;
  }, [records]);

  const rowsWithMeta = useMemo(
    () =>
      baseRows.map((row) => ({
        ...row,
        ...enrichRowQuizMeta(
          row.key,
          schoolState,
          quizCurrentBySchool,
          quizStaleBySchool
        ),
        hasIct: ictBySchool.has(row.key),
      })),
    [baseRows, quizCurrentBySchool, quizStaleBySchool, ictBySchool, schoolState]
  );

  const outreachRows = useMemo(
    () => buildOutreachRows(rowsWithMeta, manualSchools, schoolContacts),
    [rowsWithMeta, manualSchools, schoolContacts]
  );

  const filteredRows = useMemo(
    () => filterOutreachRows(outreachRows, filterId),
    [outreachRows, filterId]
  );

  const contactStats = useMemo(() => {
    const withContact = outreachRows.filter((r) =>
      normalizeWhatsApp(r.whatsapp)
    ).length;
    return { withContact, total: outreachRows.length };
  }, [outreachRows]);

  const handleSaveContact = async (contact) => {
    const key = normalizeSchool(contact.key);
    const nextContacts = [
      ...schoolContacts.filter((c) => normalizeSchool(c.key) !== key),
      { ...contact, key },
    ];
    await onSaveOutreach({ schoolContacts: nextContacts, manualSchools });
    setContactSchool(null);
  };

  const handleAddManualSchool = async () => {
    const label = newSchoolName.trim();
    if (!label) {
      alert("Enter a school name.");
      return;
    }
    const key = normalizeSchool(label);
    if (
      manualSchools.some((s) => normalizeSchool(s.key || s.label) === key) ||
      baseRows.some((r) => r.key === key)
    ) {
      alert("This school is already on the list.");
      return;
    }
    const entry = {
      key,
      label,
      whatsapp: normalizeWhatsApp(newSchoolWa) || newSchoolWa.trim(),
    };
    const nextManual = [...manualSchools, entry];
    let nextContacts = schoolContacts;
    if (entry.whatsapp) {
      nextContacts = [
        ...schoolContacts.filter((c) => normalizeSchool(c.key) !== key),
        { key, label, whatsapp: entry.whatsapp, contactName: "", notes: "" },
      ];
    }
    await onSaveOutreach({
      schoolContacts: nextContacts,
      manualSchools: nextManual,
    });
    setNewSchoolName("");
    setNewSchoolWa("");
    setShowAddSchool(false);
  };

  const handleRemoveManual = async (key) => {
    if (!window.confirm("Remove this school from the roster?")) return;
    await onSaveOutreach({
      schoolContacts: schoolContacts.filter(
        (c) => normalizeSchool(c.key) !== key
      ),
      manualSchools: manualSchools.filter(
        (s) => normalizeSchool(s.key || s.label) !== key
      ),
    });
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const text = await file.text();
      const { rows, skipped, errors } = parseSchoolContactsCsv(text);
      if (errors.length && rows.length === 0) {
        alert(errors.join("\n"));
        return;
      }
      if (rows.length === 0) {
        alert("No schools found in CSV.");
        return;
      }

      const merged = mergeCsvSchoolImport(rows, {
        schoolContacts,
        manualSchools,
        registeredKeys,
      });

      await onSaveOutreach({
        schoolContacts: merged.schoolContacts,
        manualSchools: merged.manualSchools,
      });

      const { stats } = merged;
      setImportStatus(
        `Imported ${stats.deduped} schools (${stats.duplicatesRemoved} duplicates removed). ` +
          `${stats.matchedRegistered} matched registrations, ${stats.schoolsAdded} added with 0 regs.`
      );
      if (skipped > 0) {
        setImportStatus((prev) => `${prev} ${skipped} empty rows skipped.`);
      }
      setTimeout(() => setImportStatus(null), 6000);
    } catch (err) {
      console.error(err);
      alert("Failed to import CSV. Check the file format.");
    }
  };

  const maxCount = Math.max(...outreachRows.map((r) => r.count), 1);

  const toolbar = (
    <div className="lb-toolbar">
      <div className="lb-toolbar-row">
        <div className="lb-contact-progress">
          <span className="lb-progress-label">
            Contact coverage
          </span>
          <div className="lb-progress-bar-wrap">
            <div
              className="lb-progress-bar"
              style={{
                width: `${
                  contactStats.total
                    ? (contactStats.withContact / contactStats.total) * 100
                    : 0
                }%`,
              }}
            />
          </div>
          <span className="lb-progress-stat">
            {contactStats.withContact}/{contactStats.total}
          </span>
        </div>
      </div>

      <div className="lb-toolbar-row lb-toolbar-actions">
        <select
          className="analytics-select lb-filter-select"
          value={filterId}
          onChange={(e) => setFilterId(e.target.value)}
        >
          {OUTREACH_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="export-btn"
          onClick={() => exportLeaderboardCsv(filteredRows)}
        >
          Export CSV
        </button>

        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          className="lb-csv-input"
          onChange={handleCsvImport}
        />
        <button
          type="button"
          className="refresh-btn"
          disabled={savingOutreach}
          onClick={() => csvInputRef.current?.click()}
        >
          Import CSV
        </button>

        <button
          type="button"
          className="refresh-btn"
          onClick={() => setShowAddSchool((v) => !v)}
        >
          + Add school
        </button>
      </div>

      {importStatus && (
        <p className="rename-status rename-status--done">{importStatus}</p>
      )}

      {showAddSchool && (
        <div className="lb-add-school-form">
          <input
            className="control-input"
            placeholder="School name (no registrations yet)"
            value={newSchoolName}
            onChange={(e) => setNewSchoolName(e.target.value)}
          />
          <input
            className="control-input"
            placeholder="WhatsApp (optional)"
            value={newSchoolWa}
            onChange={(e) => setNewSchoolWa(e.target.value)}
            inputMode="tel"
          />
          <button
            type="button"
            className="export-btn"
            disabled={savingOutreach}
            onClick={handleAddManualSchool}
          >
            Add to roster
          </button>
        </div>
      )}
    </div>
  );

  const renderRowActions = (row) => (
    <ContactCell row={row} onEdit={setContactSchool} />
  );

  if (outreachRows.length === 0 && manualSchools.length === 0) {
    return (
      <div className="analytics-card">
        <h3 className="analytics-card-title">School Leaderboard</h3>
        <p className="analytics-empty">No school registration data yet.</p>
        {toolbar}
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="analytics-card">
        <h3 className="analytics-card-title">School Leaderboard</h3>
        <p className="analytics-subtitle">
          {records.length} registrations · {outreachRows.length} schools
        </p>
        {toolbar}

        <div className="lb-cards">
          {filteredRows.map((row) => (
            <div
              key={row.key}
              className={`lb-card ${row.rank && row.rank <= 3 ? `lb-top-${row.rank}` : ""}`}
            >
              <div className="lb-card-head">
                <span className="lb-card-rank">
                  {row.pinned ? "📌" : row.rank ? MEDAL[row.rank - 1] ?? `#${row.rank}` : "—"}
                </span>
                <span className="lb-card-name">{row.displayName}</span>
                <span className="lb-card-count">{row.count}</span>
                <span className="lb-card-pct">{row.pct}%</span>
              </div>
              <div className="lb-card-badges">
                <QuizSeasonCell
                  effectiveCount={row.quizTeams}
                  currentCount={row.quizCurrentTeams}
                  staleCount={row.quizStaleTeams}
                  pending={row.quizPending}
                  onConfirm={
                    onQuizConfirm
                      ? (payload) => onQuizConfirm(row.key, payload)
                      : undefined
                  }
                  disabled={!onQuizConfirm || savingOutreach}
                  saving={savingOutreach}
                  compact
                />
                <IctBadge registered={row.hasIct} />
              </div>
              {renderRowActions(row)}
              {row.count === 0 && (
                <button
                  type="button"
                  className="control-unmerge-btn"
                  onClick={() => handleRemoveManual(row.key)}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {contactSchool && (
          <SchoolContactModal
            school={contactSchool}
            onSave={handleSaveContact}
            onClose={() => setContactSchool(null)}
            saving={savingOutreach}
          />
        )}
      </div>
    );
  }

  return (
    <div className="analytics-card">
      <h3 className="analytics-card-title">School Leaderboard</h3>
      <p className="analytics-subtitle">
        {records.length} registrations · {outreachRows.length} schools
      </p>
      {toolbar}

      <div className="leaderboard-table-wrapper">
        <table className="analytics-table lb-outreach-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>School</th>
              <th>Regs</th>
              <th>%</th>
              <th>Quiz</th>
              <th>ICT</th>
              <th>Contact</th>
              <th className="col-narrow">Arm.</th>
              <th className="col-narrow">Ins.</th>
              <th className="col-narrow">Zen.</th>
              <th className="col-narrow">Ort.</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr
                key={row.key}
                className={[
                  row.rank && row.rank <= 3 ? `top-${row.rank}` : "",
                  row.pinned ? "row-pinned" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <td className="rank-cell">
                  {row.pinned ? "📌" : row.rank ? MEDAL[row.rank - 1] ?? row.rank : "—"}
                </td>
                <td className="school-name-cell">{row.displayName}</td>
                <td>
                  <div className="bar-cell">
                    <div
                      className="count-bar"
                      style={{
                        width: `${Math.min(100, (row.count / maxCount) * 100)}%`,
                      }}
                    />
                    <span className="count-label">{row.count}</span>
                  </div>
                </td>
                <td>{row.pct}%</td>
                <td className="col-narrow quiz-col">
                  <QuizSeasonCell
                    effectiveCount={row.quizTeams}
                    currentCount={row.quizCurrentTeams}
                    staleCount={row.quizStaleTeams}
                    pending={row.quizPending}
                    onConfirm={
                      onQuizConfirm
                        ? (payload) => onQuizConfirm(row.key, payload)
                        : undefined
                    }
                    disabled={!onQuizConfirm || savingOutreach}
                    saving={savingOutreach}
                    compact
                  />
                </td>
                <td className="col-narrow">
                  <IctBadge registered={row.hasIct} />
                </td>
                <td className="contact-col">{renderRowActions(row)}</td>
                <td className="col-narrow">{row.byHostingSchool?.Armitage ?? 0}</td>
                <td className="col-narrow">{row.byHostingSchool?.Insignia ?? 0}</td>
                <td className="col-narrow">{row.byHostingSchool?.Zenith ?? 0}</td>
                <td className="col-narrow">{row.byHostingSchool?.Orthodox ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {contactSchool && (
        <SchoolContactModal
          school={contactSchool}
          onSave={handleSaveContact}
          onClose={() => setContactSchool(null)}
          saving={savingOutreach}
        />
      )}
    </div>
  );
}
